import { accentPalettes, blobSemantics, type ThemeMode } from '@/lib/tokens/semantic'
import LocalStorageManager from '@/lib/utils/localStorageManager'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface BlobConfig {
  id: string
  color: string
  size: number
  opacity?: number
}

export type PerformanceMode = 'high' | 'medium' | 'low' | 'disabled'

export interface BlobState {
  id: string
  seed: number
  pathProgress: number
  pathSegment: number
  speedProgress?: number
  speedSegment?: number
  speed: number
  size: number
  color: string
}

interface AnimatedBackgroundState {
  isEnabled: boolean
  speed: number
  colors: BlobConfig[]
  performanceMode: PerformanceMode
  savedBlobStates: BlobState[]
  lastDimensions: { width: number; height: number }
  currentAccent: ThemeMode
  targetColors: BlobConfig[]
  isTransitioning: boolean
}

// Helper to generate blob configs for a specific theme
function generateBlobConfigsForTheme(theme: ThemeMode): BlobConfig[] {
  const palette = accentPalettes[theme]

  // Safety check: if palette is undefined, fall back to 'warm' theme
  if (!palette) {
    console.warn(`[animatedBackgroundSlice] Invalid theme '${theme}', falling back to 'warm'`)
    return generateBlobConfigsForTheme('warm')
  }

  return palette.map((color, index) => ({
    id: String(index + 1),
    color,
    size: blobSemantics.sizes[index],
    opacity: blobSemantics.opacities[index],
  }))
}

const defaultColors: BlobConfig[] = [
  { id: '1', color: '#FD9543', size: 300, opacity: 0.75 },
  { id: '2', color: '#F996CD', size: 280, opacity: 0.8 },
  { id: '3', color: '#FF7B5A', size: 500, opacity: 0.6 },
  { id: '4', color: '#FB8FB0', size: 450, opacity: 0.65 },
  { id: '5', color: '#FFB08F', size: 410, opacity: 0.7 },
]

export const getInitialState = (): AnimatedBackgroundState => {
  const stored = loadAnimatedBackgroundSettings()
  const accentColor = stored.currentAccent ?? 'warm'
  // Use hex values from theme palette for smooth transitions (not CSS variables)
  const colors = stored.colors ?? generateBlobConfigsForTheme(accentColor)
  return {
    isEnabled: stored.isEnabled ?? true,
    speed: stored.speed ?? 1,
    colors,
    performanceMode: stored.performanceMode ?? 'high',
    savedBlobStates: stored.savedBlobStates ?? [],
    lastDimensions: stored.lastDimensions ?? { width: 0, height: 0 },
    currentAccent: accentColor,
    targetColors: colors,
    isTransitioning: false,
  }
}

export const initialState: AnimatedBackgroundState = {
  isEnabled: true,
  speed: 1,
  colors: defaultColors,
  performanceMode: 'high',
  savedBlobStates: [],
  lastDimensions: { width: 0, height: 0 },
  currentAccent: 'warm',
  targetColors: defaultColors,
  isTransitioning: false,
}

const ANIMATED_BACKGROUND_STORAGE_KEY = 'animatedBackgroundSettings'

export const loadAnimatedBackgroundSettings = (): Partial<AnimatedBackgroundState> => {
  if (typeof window === 'undefined') return {}
  try {
    const stored = LocalStorageManager.getItem(ANIMATED_BACKGROUND_STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

export const saveAnimatedBackgroundSettings = (state: AnimatedBackgroundState): void => {
  if (typeof window === 'undefined') return
  try {
    LocalStorageManager.setItem(ANIMATED_BACKGROUND_STORAGE_KEY, JSON.stringify(state))
  } catch {}
}

const animatedBackgroundSlice = createSlice({
  name: 'animatedBackground',
  initialState: getInitialState(),
  reducers: {
    setEnabled: (state, action: PayloadAction<boolean>) => {
      state.isEnabled = action.payload
      saveAnimatedBackgroundSettings(state)
    },
    setSpeed: (state, action: PayloadAction<number>) => {
      state.speed = Math.max(0.1, Math.min(10.0, action.payload))
      saveAnimatedBackgroundSettings(state)
    },
    setPerformanceMode: (state, action: PayloadAction<PerformanceMode>) => {
      state.performanceMode = action.payload
      saveAnimatedBackgroundSettings(state)
    },
    updateBlobColor: (state, action: PayloadAction<{ id: string; color: string }>) => {
      const blob = state.colors.find((b) => b.id === action.payload.id)
      if (blob) {
        blob.color = action.payload.color
        saveAnimatedBackgroundSettings(state)
      }
    },
    updateBlobSize: (state, action: PayloadAction<{ id: string; size: number }>) => {
      const blob = state.colors.find((b) => b.id === action.payload.id)
      if (blob) {
        blob.size = Math.max(50, Math.min(200, action.payload.size))
        saveAnimatedBackgroundSettings(state)
      }
    },
    updateBlobOpacity: (state, action: PayloadAction<{ id: string; opacity: number }>) => {
      const blob = state.colors.find((b) => b.id === action.payload.id)
      if (blob) {
        blob.opacity = Math.max(0.1, Math.min(1.0, action.payload.opacity))
        saveAnimatedBackgroundSettings(state)
      }
    },
    addBlob: (state, action: PayloadAction<Omit<BlobConfig, 'id'>>) => {
      const newBlob: BlobConfig = {
        ...action.payload,
        id: Date.now().toString(),
      }
      state.colors.push(newBlob)
      saveAnimatedBackgroundSettings(state)
    },
    removeBlob: (state, action: PayloadAction<string>) => {
      state.colors = state.colors.filter((blob) => blob.id !== action.payload)
      saveAnimatedBackgroundSettings(state)
    },
    updateBlobStates: (state, action: PayloadAction<BlobState[]>) => {
      state.savedBlobStates = action.payload
    },
    saveBlobStates: (
      state,
      action: PayloadAction<{ blobs: BlobState[]; dimensions: { width: number; height: number } }>
    ) => {
      state.savedBlobStates = action.payload.blobs
      state.lastDimensions = action.payload.dimensions
      saveAnimatedBackgroundSettings(state)
    },
    clearBlobStates: (state) => {
      state.savedBlobStates = []
      state.lastDimensions = { width: 0, height: 0 }
      saveAnimatedBackgroundSettings(state)
    },
    resetToDefaults: (state) => {
      state.isEnabled = initialState.isEnabled
      state.speed = initialState.speed
      state.colors = [...initialState.colors]
      state.performanceMode = initialState.performanceMode
      saveAnimatedBackgroundSettings(state)
    },
    loadSettings: (state) => {
      const stored = loadAnimatedBackgroundSettings()

      if (Object.keys(stored).length > 0) {
        Object.assign(state, stored)
      }
    },
    initializeWithCookieState: (
      state,
      action: PayloadAction<{ blobs: BlobState[]; dimensions: { width: number; height: number } }>
    ) => {
      state.savedBlobStates = action.payload.blobs
      state.lastDimensions = action.payload.dimensions
    },
    setAccentColor: (state, action: PayloadAction<ThemeMode>) => {
      const themeMode = action.payload
      if (state.currentAccent === themeMode) return

      console.log('[animatedBackgroundSlice] setAccentColor called:', {
        oldAccent: state.currentAccent,
        newAccent: themeMode,
        targetColors: generateBlobConfigsForTheme(themeMode),
        timestamp: new Date().toISOString(),
      })

      state.currentAccent = themeMode
      state.targetColors = generateBlobConfigsForTheme(themeMode)
      state.isTransitioning = true
      saveAnimatedBackgroundSettings(state)
    },
    updateColorsForTransition: (state, action: PayloadAction<BlobConfig[]>) => {
      state.colors = action.payload
    },
    completeTransition: (state) => {
      state.colors = state.targetColors
      state.isTransitioning = false
      saveAnimatedBackgroundSettings(state)
    },
  },
})

export const {
  setEnabled,
  setSpeed,
  setPerformanceMode,
  updateBlobColor,
  updateBlobSize,
  updateBlobOpacity,
  addBlob,
  removeBlob,
  updateBlobStates,
  saveBlobStates,
  clearBlobStates,
  resetToDefaults,
  loadSettings,
  initializeWithCookieState,
  setAccentColor,
  updateColorsForTransition,
  completeTransition,
} = animatedBackgroundSlice.actions

export default animatedBackgroundSlice.reducer
